import React, { useState, useCallback } from 'react';
import { Checkbox } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import PermissionComp from '../permission.component';
// eslint-disable-next-line import/no-named-as-default
import KrAlignDependance from '../krAlignDependence';
import AddKrTag from '../../../../../krTags/components/addkrTag.component';
import Customokr from '../../../../custom_fields/customobj/Customokr';
import KrRelationshipWrapper from '../krRelationshipWrapper';
import KrMilestonesSection from '../KrMilestonesSection';
import { AddFinancialTargetsButton } from '../../AddFinancialTargetsButton';
import type {
  CascadeKRCompProps,
  MetricObj,
  GoalDetails,
  AddOkrData,
} from './cascadeKrTypes';

const StyledCheckbox = styled(Checkbox)`
  .MuiButtonBase-root {
    width: 24px;
    height: 24px;
    margin-left: -5px;
    padding: 0;
  }
  input {
    display: inline-block !important;
  }
  svg {
    width: 24px;
    height: 24px;
  }
`;

export type AdditionalSectionChanges = {
  metricObj?: Partial<MetricObj>;
  customData?: Record<string, unknown>;
  customInputData?: Record<string, unknown>;
  isRequiredCustomOkr?: string[];
  isCustomSave?: string[];
  tags?: string[];
  selectednlplTagsName?: string[];
  krAlignSelected?: unknown[];
  krDependenciesSelected?: unknown[];
  teamPermissionSelected?: unknown[];
  bizPermissionSelected?: unknown[];
  permissionstate?: number[];
  milestones?: Array<{ id?: number; date: number; label?: string; target: number }>;
};

export type CascadeAdditionalSectionProps = {
  metricObj: MetricObj;
  item: MetricObj;
  goalDetails: GoalDetails;
  addOkrData: AddOkrData;
  krText: string;
  customData: Record<string, unknown>;
  groupKRCustomFields: unknown;
  renderError: (field: string) => React.ReactNode;
  isCustomSave: string[];
  props: CascadeKRCompProps;
  nplTags: string[];
  selectedNlplTags: string[];
  selectedNlplTagsName: string[];
  tagsList: string[];
  teamPermissionSelected: unknown[];
  bizPermissionSelected: unknown[];
  permissionState: number[];
  milestones: Array<{ id?: number; date: number; label?: string; target: number }>;
  onChange: (changes: AdditionalSectionChanges) => void;
  saveKr: (callback?: ((data?: Record<string, unknown>) => void) | null) => void;
};

export const CascadeAdditionalSection: React.FC<CascadeAdditionalSectionProps> = ({
  metricObj,
  item,
  goalDetails,
  addOkrData,
  krText,
  customData,
  groupKRCustomFields,
  renderError,
  isCustomSave: initialIsCustomSave,
  props,
  nplTags: initialNplTags,
  selectedNlplTags: initialSelectednlplTags,
  selectedNlplTagsName: initialSelectednlplTagsName,
  tagsList: initialTagsList,
  teamPermissionSelected: initialTeamPermissionSelected,
  bizPermissionSelected: initialBizPermissionSelected,
  permissionState: initialPermissionstate,
  milestones,
  onChange,
  saveKr,
}) => {
  const { t } = useTranslation();

  // Local state - UI
  const [permissionHelpPopUp, setPermissionHelpPopUp] = useState(false);

  // Local state - Tags
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tags, setTags] = useState<string[]>([]);
  const [nplTags] = useState<string[]>(initialNplTags || []);
  const [selectednlplTags] = useState<string[]>(initialSelectednlplTags || []);
  const [selectednlplTagsName, setSelectednlplTagsName] = useState<string[]>(
    initialSelectednlplTagsName || []
  );
  const [tagsList] = useState<string[]>(initialTagsList || []);

  // Local state - Custom fields
  const [isCustomSave, setIsCustomSave] = useState<string[]>(initialIsCustomSave);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isRequirdCustomOkr, setIsRequirdCustomOkr] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customInputData, setCustomInputData] = useState<Record<string, unknown>>({});

  // Local state - Permissions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [teamPermissionSelected, setTeamPermissionSelected] = useState<unknown[]>(
    initialTeamPermissionSelected
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bizPermissionSelected, setBizPermissionSelected] = useState<unknown[]>(
    initialBizPermissionSelected
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [permissionstate, setPermissionstate] = useState<number[]>(initialPermissionstate);

  // Event handlers
  const handleExcludeObjectiveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chk = e.target.checked;
      onChange({ metricObj: { progress_consider: chk } });
    },
    [onChange]
  );

  const handlePermissionData = useCallback(
    (perData: number[]) => {
      setPermissionstate(perData);
      onChange({ permissionstate: perData });
    },
    [onChange]
  );

  const handleTeamPermissionData = useCallback(
    (teamPerData: unknown[]) => {
      setTeamPermissionSelected(teamPerData);
      onChange({ teamPermissionSelected: teamPerData });
    },
    [onChange]
  );

  const handleBizPermissionData = useCallback(
    (bizPerData: unknown[]) => {
      setBizPermissionSelected(bizPerData);
      onChange({ bizPermissionSelected: bizPerData });
    },
    [onChange]
  );

  const handleSaveAlignArr = useCallback(
    (alignData: unknown[]) => {
      onChange({ krAlignSelected: alignData });
    },
    [onChange]
  );

  const handleSaveDependentArr = useCallback(
    (dependentData: unknown[]) => {
      onChange({ krDependenciesSelected: dependentData });
    },
    [onChange]
  );

  const handleSaveTag = useCallback(
    (childData: string[]) => {
      setTags(childData);
      onChange({ tags: childData });
    },
    [onChange]
  );

  const handleMilestonesChange = useCallback(
    (newMilestones: Array<{ id?: number; date: number; label?: string; target: number }>) => {
      onChange({ milestones: newMilestones });
    },
    [onChange]
  );

  const handleUpdateTags = useCallback(
    (Newtags: string[]) => {
      setSelectednlplTagsName(Newtags);
      onChange({ selectednlplTagsName: Newtags });
    },
    [onChange]
  );

  const handleSaveCustom = useCallback(
    (isVal: Record<
      string,
      { customFields?: Record<string, { is_required?: string; text?: unknown }> }
    >) => {
      const arr: string[] = [];
      let j = 0;
      const keyArrCategory: string[] = [];
      const valArray: unknown[] = [];

      if (isVal) {
        Object.keys(isVal).forEach((v) => {
          j++;
          const { customFields } = isVal[v];
          if (customFields) {
            Object.keys(customFields).forEach((objv) => {
              keyArrCategory.push(objv);
              const field = customFields[objv];
              const isEmptyText =
                field.text === undefined ||
                (Array.isArray(field.text) && field.text.length === 0);
              if (field.is_required === '1' && isEmptyText) {
                arr.push(objv, v);
              }
              if (field.text) {
                valArray.push(field.text);
              } else {
                valArray.push('');
              }
            });
          }
          if (j === Object.keys(isVal).length) {
            const finalDataArray: Record<string, unknown>[] = [];
            for (let i = 0; i < valArray.length; i += 1) {
              if (Array.isArray(valArray[i])) {
                (valArray[i] as { name: string }[]).forEach((val) => {
                  finalDataArray.push({ id: keyArrCategory[i], val: val.name, object_type: '6' });
                });
              } else {
                finalDataArray.push({ id: keyArrCategory[i], val: valArray[i], object_type: '6' });
              }
            }
            setIsRequirdCustomOkr([...arr]);
            onChange({
              isRequiredCustomOkr: [...arr],
            });
          }
        });
      }
    },
    [onChange]
  );

  const handleRequiredCustomOkrFiled = useCallback(
    (isVal: Record<string, unknown>, id: string) => {
      const custId = isCustomSave.find((u) => u === id);
      const fr = isCustomSave.filter((u) => u !== custId);

      setIsCustomSave(fr);
      setCustomInputData(isVal);

      handleSaveCustom(
        isVal as Record<
          string,
          { customFields?: Record<string, { is_required?: string; text?: unknown }> }
        >
      );

      onChange({
        isCustomSave: fr,
        customInputData: isVal,
      });
    },
    [isCustomSave, handleSaveCustom, onChange]
  );

  const handleCustomDataChange = useCallback(
    (newData: Record<string, unknown>) => {
      onChange({ customData: newData });
    },
    [onChange]
  );

  return (
    <div>
      {customData && Object.keys(customData).length > 0 && (
        <div className="breakline" style={{ marginBottom: '-10px' }} aria-hidden="true" />
      )}
      {(addOkrData.groupKRCustomFields || groupKRCustomFields) && (
        <Customokr
          customData={customData}
          isRequiredCustomOkrFiled={handleRequiredCustomOkrFiled}
          onCustomDataChange={handleCustomDataChange}
          customFiledRequired
          renderError={renderError}
          isCustomSave={isCustomSave}
        />
      )}
      <div className="breakline" />
      <KrRelationshipWrapper {...props} metric={item} saveKr={saveKr} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <KrMilestonesSection
        {...(props as any)}
        source="9"
        metric={metricObj as any}
        metricState={{ metricObj } as any}
        onChange={(newMilestones) =>
          handleMilestonesChange(
            newMilestones.map((m) => ({
              ...m,
              id: m.id ? Number(m.id) : undefined,
            }))
          )
        }
      />
      <KrAlignDependance
        {...props}
        saveDependentArr={handleSaveDependentArr}
        saveAlignArr={handleSaveAlignArr}
        krAlingArr={item.kr_aligns}
        krDependenciesArr={item.kr_dependent}
      />
      <AddFinancialTargetsButton
        objectType="kr"
        objectId={item?.id != null ? Number(item.id) : undefined}
        ownerUserId={item.real_owner ?? goalDetails.owner_id}
      />
      <AddKrTag
        metric={item}
        goalId={goalDetails.id}
        taghandler={handleSaveTag}
        nplTags={nplTags}
        selectednlplTags={selectednlplTags}
        selectednlplTagsName={selectednlplTagsName}
        updateTags={handleUpdateTags}
        tagsList={tagsList}
        searchText={krText}
      />
      <div className="fieldcontent">
        <StyledCheckbox
          id="exclude_objective"
          label={t('Exclude from objective progress calculation')}
          isChecked={metricObj.progress_consider}
          onChange={handleExcludeObjectiveChange}
        />
      </div>
      <div className="fieldcontent">
        {!(goalDetails && goalDetails.all_metrics === 1) && (
          <div className="pT10">
            <div className="viewPermissionArea">
              <span>
                {t('View permissions in addition to')}{' '}
                {goalDetails.type === 1 ? ` ${t('team')} ` : ` ${t('owner')} `}
                <div
                  className="metricTargetHelptext permissionHelpText"
                  onMouseEnter={() => {
                    setPermissionHelpPopUp(true);
                  }}
                  onMouseLeave={() => {
                    setPermissionHelpPopUp(false);
                  }}
                />
                {permissionHelpPopUp && (
                  <div className="basic-popup">
                    {t(
                      'You can give your whole organization permission to view your Key Result, or just your team and managers'
                    )}
                  </div>
                )}
              </span>
            </div>
            <div className="pB10">
              <PermissionComp
                {...props}
                teamPermissionData={handleTeamPermissionData}
                bizPermissionData={handleBizPermissionData}
                permissionData={handlePermissionData}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
